import React from 'react';
import { VersionResponse } from '../types';

interface VersionDetailsModalProps {
  isOpen: boolean;
  version: VersionResponse | null;
  onClose: () => void;
}

const ListSection: React.FC<{ title: string; items: string[] | null; color?: string }> = ({ title, items, color }) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: color || 'inherit' }}>{title}</h4>
      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.8 }}>
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    </div>
  );
};

const VersionDetailsModal: React.FC<VersionDetailsModalProps> = ({ isOpen, version, onClose }) => {
  if (!isOpen || !version) return null;

  const releasedDate = version.released_at
    ? new Date(version.released_at).toLocaleDateString()
    : 'Unreleased';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '80vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>
            {version.version}
            {version.codename && <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: '8px' }}>"{version.codename}"</span>}
          </h2>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* Status badges */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
            {version.is_latest && <span className="status-badge status-active">latest</span>}
            {version.is_stable && <span className="status-badge status-active">stable</span>}
            {version.is_lts && <span className="status-badge status-provisioning">LTS</span>}
            {version.is_security_patch && <span className="status-badge status-provisioning">security</span>}
            {version.is_deprecated && <span className="status-badge status-suspended">deprecated</span>}
          </div>

          {/* Metadata grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px', fontSize: '13px' }}>
            <div><span style={{ opacity: 0.5 }}>Released:</span> {releasedDate}</div>
            <div><span style={{ opacity: 0.5 }}>Created by:</span> {version.created_by || '—'}</div>
            <div><span style={{ opacity: 0.5 }}>API Image:</span> {version.api_image}</div>
            <div><span style={{ opacity: 0.5 }}>UI Image:</span> {version.ui_image}</div>
            {version.minimum_upgrade_from && (
              <div><span style={{ opacity: 0.5 }}>Min upgrade from:</span> {version.minimum_upgrade_from}</div>
            )}
            {version.python_version && (
              <div><span style={{ opacity: 0.5 }}>Python:</span> {version.python_version}</div>
            )}
            {version.postgres_version && (
              <div><span style={{ opacity: 0.5 }}>PostgreSQL:</span> {version.postgres_version}</div>
            )}
            {version.git_tag && (
              <div><span style={{ opacity: 0.5 }}>Git tag:</span> {version.git_tag}</div>
            )}
            {version.support_ends_at && (
              <div><span style={{ opacity: 0.5 }}>Support ends:</span> {new Date(version.support_ends_at).toLocaleDateString()}</div>
            )}
            {version.deprecated_at && (
              <div><span style={{ opacity: 0.5 }}>Deprecated:</span> {new Date(version.deprecated_at).toLocaleDateString()}</div>
            )}
          </div>

          {/* Description */}
          {version.description && (
            <div style={{ marginBottom: '20px', fontSize: '14px', lineHeight: 1.6 }}>
              {version.description}
            </div>
          )}

          {/* Lists */}
          <ListSection title="New Features" items={version.new_features} />
          <ListSection title="Breaking Changes" items={version.breaking_changes} color="var(--warning)" />
          <ListSection title="Security Fixes" items={version.security_fixes} />
          <ListSection title="Changelog" items={version.changelog} />
          <ListSection title="Deprecated Features" items={version.deprecated_features} color="var(--warning)" />

          {/* Migrations */}
          {version.migrations && version.migrations.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600 }}>Migrations</h4>
              <div style={{ background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace' }}>
                {version.migrations.map((m, i) => <div key={i}>{m}</div>)}
              </div>
              {version.migration_timeout_seconds && (
                <div style={{ fontSize: '12px', opacity: 0.5, marginTop: '4px' }}>
                  Timeout: {version.migration_timeout_seconds}s
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default VersionDetailsModal;
